import { useState, useRef, useEffect, useMemo } from "react";
import { Badge } from "reactstrap";
import {
  FiTrash2,
  FiEdit2,
  FiPlus,
  FiChevronRight,
  FiChevronDown,
  FiCheck,
  FiX,
  FiChevronUp,
} from "react-icons/fi";
import { FaLayerGroup, FaRegDotCircle } from "react-icons/fa";
import { CustomSelect } from "./components/CustomSelect";
import {
  returnName,
  returnPlaceHolder,
  returnSelectOptions,
  returnUniqueKey,
  ticketTypeSelectOptions,
  typeSelectOptions,
} from "./utils";
import { useSelector } from "react-redux";
import {
  createSubServiceMapping,
  deleteSubServiceMapping,
  updateSubServiceType,
} from "../../../../../../store/slices/subServicesSlice";
import { useDispatch } from "react-redux";
import {
  createCategoryMapping,
  createSubCategoryMapping,
  deleteCatergoryMapping,
  updateCategoryType,
} from "../../../../../../store/slices/categories";
import {
  deletePrgramServiceMapping,
  updateServiceType,
} from "../../../../../../store/slices/servicesSlice";
import {
  deleteSubCatergoryMapping,
  updateSubCategoryType,
} from "../../../../../../store/slices/subCategorySlice";

const TreeNode = ({
  node,
  parent,
  depth,
  allCategories,
  allServices,
  allSubCategories,
  allSubServices,
  getAllProgramsData,
  previousParent,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(node.service_name);
  const dispatch = useDispatch();
  const programDetails = useSelector((state) => state.programs.programDetail);
  const [editType, setEditType] = useState(node?.type ?? "grouped");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const parseTicketType = (tt) => {
    try {
      if (Array.isArray(tt)) return tt;
      if (typeof tt === "string") return JSON.parse(tt);
    } catch (e) {}
    return [];
  };
  const [ticketType, setTicketType] = useState(() => parseTicketType(node.ticket_type));

  // Sync ticketType when node refreshes after a save (compare by serialized value)
  const ticketTypeKey = typeof node.ticket_type === "string" ? node.ticket_type : JSON.stringify(node.ticket_type);
  useEffect(() => {
    setTicketType(parseTicketType(node.ticket_type));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketTypeKey]);

  // Derived values — never mutated directly by user actions
  const isGrouped = node?.type === "grouped";
  const options = useMemo(() => {
    if (depth === 0) return node?.sub_services ?? [];
    if (depth === 1) return node?.categories ?? [];
    if (depth === 2) return node?.sub_categories ?? [];
    return [];
  }, [node, depth]);
  const createSubServiceMappingData = async () => {
    let payload = {
      plan_slug: programDetails.slug_name,
      service_slug: node?.slug_name,
      type: node?.type ?? "grouped",
      sub_service_slug: [{ sub_service_slug: editName, type: editType }],
      ticketType,
    };
    let data = await dispatch(createSubServiceMapping(payload));

    getAllProgramsData();
  };
  const createCategorieseMappingData = async () => {
    let payload = {
      plan_slug: programDetails.slug_name,
      service_slug: parent.slug_name,
      sub_service_slug: node.slug_name,
      category_slug: [{ category_slug: editName, type: editType }],
      ticketType,
    };
    let data = await dispatch(createCategoryMapping(payload));

    getAllProgramsData();
  };
  const createSubCategorieseMappingData = async () => {
    let payload = {
      plan_slug: programDetails.slug_name,
      service_slug: previousParent.slug_name,
      sub_service_slug: parent.slug_name,
      category_slug: node.slug_name,
      subcategory_slug: [{ subcategory_slug: editName, type: editType }],
      ticketType,
    };
    let data = await dispatch(createSubCategoryMapping(payload));

    getAllProgramsData();
  };
  const handleSave = () => {
    if (editName && editType) {
      if (depth === 0) {
        createSubServiceMappingData();
      } else if (depth === 1) {
        createCategorieseMappingData();
      } else if (depth === 2) {
        createSubCategorieseMappingData();
      }
      setTicketType([]);
      setIsAdding(false);
    } else {
      alert(returnPlaceHolder(depth));
    }
  };

  const handleCancel = () => {
    setEditName(node.slug_name);
    setEditType(node.type);
    try {
      if (Array.isArray(node.ticket_type)) {
        setTicketType(node.ticket_type);
      } else if (typeof node.ticket_type === "string") {
        setTicketType(JSON.parse(node.ticket_type));
      } else {
        setTicketType([]);
      }
    } catch (e) {
      setTicketType([]);
    }
    setIsAdding(false);
    setIsEditing(false);
  };

  const hasChildren = options.length > 0;

  const handleAddClick = () => {
    setTicketType(ticketTypeSelectOptions.map((opt) => opt.value));
    setIsAdding(true);
  };
  const handleEditSave = async () => {
    if (depth === 0) {
      await dispatch(
        updateServiceType({
          id: node?.service_map_id,
          type: editType,
          ticketType,
        }),
      );
    } else if (depth === 1) {
      await dispatch(
        updateSubServiceType({
          id: node?.sub_service_map_id,
          type: editType,
          ticketType,
        }),
      );
    } else if (depth === 2) {
      await dispatch(
        updateCategoryType({
          id: node.category_map_id,
          type: editType,
          ticketType,
        }),
      );
    } else if (depth === 3) {
      await dispatch(
        updateSubCategoryType({
          id: node?.sub_category_map_id,
          type: editType,
          ticketType,
        }),
      );
    }
    setIsEditing(false);
    getAllProgramsData();
  };
  const handleDelete = async () => {
    if (depth === 0) {
      await dispatch(deletePrgramServiceMapping({ ID: node?.service_map_id }));
    } else if (depth === 1) {
      await dispatch(deleteSubServiceMapping({ ID: node?.sub_service_map_id }));
    } else if (depth === 2) {
      dispatch(deleteCatergoryMapping({ ID: node?.category_map_id }));
    } else if (depth === 3) {
      await dispatch(
        deleteSubCatergoryMapping({ ID: node?.sub_category_map_id }),
      );
    }
    getAllProgramsData();
  };

  const handleEditClick = () => {
    setEditType(node.type);
    try {
      if (Array.isArray(node.ticket_type)) {
        setTicketType(node.ticket_type);
      } else if (typeof node.ticket_type === "string") {
        setTicketType(JSON.parse(node.ticket_type));
      } else {
        setTicketType([]);
      }
    } catch (e) {
      setTicketType([]);
    }
    setIsEditing(true);
  };
  return (
    <div>
      <div
        className="tree-node-row"
        style={{
          paddingLeft: `${depth * 28 + 12}px`,
          padding: `0.75rem ${depth * 28 + 12}px 0.75rem ${depth * 28 + 12}px`,
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          borderRadius: "0.375rem",
          transition: "background-color 0.2s ease",
          backgroundColor: "transparent",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(0, 123, 255, 0.05)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        {/* Expand/Collapse toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="action-btn"
          disabled={!isGrouped || !hasChildren}
          style={{ padding: "0.125rem" }}
        >
          {isGrouped && hasChildren ? (
            isCollapsed ? (
              <FiChevronRight size={16} />
            ) : (
              <FiChevronDown size={16} />
            )
          ) : (
            <span style={{ width: 16, height: 16, display: "inline-block" }} />
          )}
        </button>

        {isGrouped ? (
          <FaLayerGroup size={16} color="#007bff" />
        ) : (
          <FaRegDotCircle size={16} color="#28a745" />
        )}
        <>
          <span style={{ flex: 1, fontSize: "0.875rem", fontWeight: 500 }}>
            {returnName(node, depth)}
          </span>
          {isAdding && (
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                gap: "0.75rem",
                backgroundColor: "#f0f4f8",
                padding: "0.75rem 1rem",
                borderRadius: "0.375rem",
                border: "1px solid #e0e8f0",
              }}
            >
              <CustomSelect
                value={editName}
                options={returnSelectOptions(
                  depth + 1,
                  allServices,
                  allSubServices,
                  allCategories,
                  allSubCategories,
                )}
                onChange={setEditName}
                placeholder={returnPlaceHolder(depth)}
                style={{ width: "150px" }}
              />
              <CustomSelect
                value={editType}
                options={typeSelectOptions}
                onChange={(v) => setEditType(v)}
                placeholder="Select type..."
                style={{ width: "9rem" }}
              />
              <CustomSelect
                value={ticketType}
                options={ticketTypeSelectOptions}
                onChange={(v) => setTicketType(v)}
                placeholder="Select Ticket type..."
                style={{ width: "100px" }}
                isMulti
              />
              <button onClick={handleSave} className="action-btn save">
                <FiCheck size={16} />
              </button>
              <button onClick={handleCancel} className="action-btn cancel">
                <FiX size={16} />
              </button>
            </div>
          )}
          {isEditing && (
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                gap: "0.75rem",
                backgroundColor: "#fff3cd",
                padding: "0.75rem 1rem",
                borderRadius: "0.375rem",
                border: "1px solid #ffc107",
              }}
            >
              <CustomSelect
                value={editType}
                options={typeSelectOptions}
                onChange={(v) => setEditType(v)}
                placeholder="Select type..."
                style={{ width: "9rem" }}
              />
              <CustomSelect
                value={ticketType}
                options={ticketTypeSelectOptions}
                onChange={(v) => setTicketType(v)}
                placeholder="Select Ticket type..."
                style={{ width: "100px" }}
                isMulti
              />
              <button
                onClick={() => handleEditSave()}
                className="action-btn save"
              >
                <FiCheck size={16} />
              </button>
              <button onClick={handleCancel} className="action-btn cancel">
                <FiX size={16} />
              </button>
            </div>
          )}
          {!isAdding && !isEditing && (
            <>
              <Badge
                pill
                className={`badge-green ${isGrouped ? "type-badge-grouped" : "type-badge-individual"}`}
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  border: "none",
                }}
              >
                {/* {node.type} */}
              </Badge>
              {ticketType.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "0.35rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {ticketType.map((type) => {
                    const option = ticketTypeSelectOptions.find(
                      (opt) => opt.value === type,
                    );
                    return (
                      <Badge
                        key={type}
                        pill
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: "#0dcaf0",
                          color: "white",
                          border: "none",
                          padding: "0.35rem 0.65rem",
                          boxShadow: "0 1px 3px rgba(13, 202, 240, 0.2)",
                        }}
                      >
                        {option ? option.label : type}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Actions */}
              <div
                className="node-actions"
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                {isGrouped && depth < 3 && (
                  <button
                    onClick={() => handleAddClick()}
                    className="action-btn"
                    title="Add child"
                    style={{
                      padding: "0.375rem 0.5rem",
                      borderRadius: "0.375rem",
                      border: "1px solid #dee2e6",
                      backgroundColor: "#f8f9fa",
                      color: "#007bff",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#007bff";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                      e.currentTarget.style.color = "#007bff";
                    }}
                  >
                    <FiPlus size={14} />
                  </button>
                )}
                <button
                  onClick={() => handleEditClick()}
                  className="action-btn"
                  title="Edit"
                  style={{
                    padding: "0.375rem 0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #dee2e6",
                    backgroundColor: "#f8f9fa",
                    color: "#ffc107",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffc107";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                    e.currentTarget.style.color = "#ffc107";
                  }}
                >
                  <FiEdit2 size={14} />
                </button>
                <button
                  onClick={() => handleDelete()}
                  className="action-btn delete"
                  title="Delete"
                  style={{
                    padding: "0.375rem 0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #dee2e6",
                    backgroundColor: "#f8f9fa",
                    color: "#dc3545",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#dc3545";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                    e.currentTarget.style.color = "#dc3545";
                  }}
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            </>
          )}{" "}
        </>
      </div>

      {/* Children */}
      {isGrouped && !isCollapsed && hasChildren && (
        <div style={{ position: "relative" }}>
          <div className="tree-line" style={{ left: `${depth * 28 + 24}px` }} />
          {options.map((child, index) => (
            <TreeNode
              key={`${returnUniqueKey(child, node, parent, previousParent, depth, ticketType)}`}
              // key={JSON.stringify(node)}
              node={child}
              parent={node}
              previousParent={parent}
              depth={depth + 1}
              allCategories={allCategories}
              allServices={allServices}
              allSubCategories={allSubCategories}
              allSubServices={allSubServices}
              getAllProgramsData={getAllProgramsData}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeNode;
